import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import CircularProgress from "@mui/material/CircularProgress";

import useNotification from "../../hooks/notification";
import Camera from "./Camera";
import ImageUploadButtons from "../../views/capture/Images/ImageUploadButtons";

const cameraType = "default";

export default function ImageInput({ label, imageUrl, onUpload, disabled, allowLibrary = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { notifyError } = useNotification();

  const toggleOpen = () => setOpen(v => !v);

  const onSuccess = () => {
    setLoading(false);
  };

  const onFailure = errors => {
    notifyError(errors);
    setLoading(false);
  };

  const uploadFile = file => {
    const result = onUpload(file, { onSuccess, onFailure });
    return Promise.resolve(result);
  };

  const handleDefaultUpload = e => {
    const file = e.target.files[0];
    setLoading(true);
    uploadFile(file).then(() => {
      setLoading(false);
    });
  };

  const handleLibraryUpload = e => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;

    setLoading(true);
    files
      .reduce((chain, file) => chain.then(() => uploadFile(file)), Promise.resolve())
      .finally(() => setLoading(false));
  };

  const handleTakePhoto = dataUri => {
    setLoading(true);
    fetch(dataUri)
      .then(res => res.blob())
      .then(file => {
        toggleOpen();
        uploadFile(file);
      })
      .catch(error => {
        setLoading(false);
        notifyError(error?.message || "Can't upload file");
      });
  };

  if (open) {
    return <Camera takePhoto={handleTakePhoto} toggleOpen={toggleOpen} />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      height="100%"
      justifyContent="space-between"
    >
      {cameraType === "default" && allowLibrary && (
        <ImageUploadButtons
          onUpload={handleDefaultUpload}
          onLibraryUpload={handleLibraryUpload}
          disabled={disabled}
          loading={loading}
          sx={{ mt: 0 }}
        />
      )}
      {cameraType === "default" && !allowLibrary && (
        <Button
          sx={{ height: 40, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
          variant="outlined"
          color="primary"
          fullWidth
          disabled={disabled || loading}
          component="label"
          startIcon={loading && <CircularProgress size={20} />}
        >
          <AddAPhotoIcon />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            disabled={disabled}
            onChange={handleDefaultUpload}
          />
        </Button>
      )}
      {cameraType === "react-webcam" && (
        <Button
          sx={{ height: 40, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
          variant="outlined"
          color="primary"
          fullWidth
          disabled={disabled || loading}
          data-id="camera-button"
          component="label"
          onClick={toggleOpen}
          startIcon={loading && <CircularProgress size={20} />}
        >
          <AddAPhotoIcon />
        </Button>
      )}
    </Box>
  );
}
